/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { memo, useCallback, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
];

export const LanguageSwitcher = memo(() => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const getNormalizedLanguage = useCallback(
    (language?: string) => {
      const baseLanguage = (language || i18n.resolvedLanguage || i18n.language || 'en')
        .toLowerCase()
        .split('-')[0];

      const isSupported = SUPPORTED_LANGUAGES.some((lang) => lang.code === baseLanguage);
      return isSupported ? baseLanguage : 'en';
    },
    [i18n.language, i18n.resolvedLanguage],
  );

  const [selectedLanguage, setSelectedLanguage] = useState(() =>
    getNormalizedLanguage(),
  );

  useEffect(() => {
    const nextLanguage = getNormalizedLanguage();
    setSelectedLanguage((prev) => (prev === nextLanguage ? prev : nextLanguage));
  }, [getNormalizedLanguage]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageSelect = (code: string) => {
    setSelectedLanguage(code);
    i18n.changeLanguage(code);
    setIsOpen(false);
  };

  const currentLanguageLabel =
    SUPPORTED_LANGUAGES.find((lang) => lang.code === selectedLanguage)?.label || 'English';

  return (
    <div className="relative inline-block text-left" ref={dropdownRef} data-testid="container-view">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-40 px-3 py-2 text-sm transition-colors duration-200 
                   bg-white border border-gray-300 text-gray-900 rounded-md
                   focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                   dark:bg-slate-700 dark:border-slate-500 dark:text-gray-100"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{currentLanguageLabel}</span>
        <ChevronDown
          size={16}
          className="text-gray-500 dark:text-gray-400"
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 origin-top-right overflow-hidden shadow-lg 
                        bg-white border border-gray-200 
                        dark:bg-slate-700 dark:border-gray-600">
          <ul
            className="text-sm text-gray-700 dark:text-gray-100 flex flex-col py-1"
            role="listbox"
          >
            {SUPPORTED_LANGUAGES.map((lang) => {
              const isSelected = selectedLanguage === lang.code;
              return (
                <li key={lang.code} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleLanguageSelect(lang.code)}
                    className={`block w-full px-3 py-2 text-left transition-colors duration-150
                      hover:bg-blue-300 hover:text-slate-900 
                      dark:hover:bg-blue-300 dark:hover:text-slate-900
                      ${isSelected ? 'font-medium' : ''}
                    `}
                  >
                    {lang.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
});

LanguageSwitcher.displayName = 'LanguageSwitcher';